import Loader from "react-loader";
import { useResizeDetector } from "components/hooks/resize-detector";
import { PieChartWithLegends } from "components/molecules/PieChartWithLegends.molecule";
import { QUERY_KEY_MAPPING } from "pages/inventoryview/details/search/InventoryView.Details.Search.Options";
import { VictoryLabel, VictoryTooltip } from "victory";
import { useTranslation } from "react-i18next";
import Colors from "styles/colors";
import { Alert } from "components/atoms/Alert.atom";
import { convertCountToLocaleString } from "utils/html-utils";
import { Modal } from "react-bootstrap";
import { useState } from "react";
import { InventoryLocationsList } from "./InventoryLocationRow";
import { FontSize, Text } from "components/atoms/Text.atom";
import { useGateReleaseThresholdsPieChart } from "pages/inventoryview/react-queries/GateReleaseThresholdHooks";
import { TimeOnSiteDuration } from "api/consts";

const calculatePercentage = (vinCount: number, total: number) => {
  return ((vinCount / total) * 100).toFixed(1);
};

const CustomTooltip = (props: any) => {
  return (
    <g>
      <VictoryTooltip
        {...props}
        orientation="top"
        pointerLength={10}
        flyoutWidth={40}
        flyoutStyle={{
          stroke: "none",
          strokeWidth: 2,
          fill: "transparent",
        }}
        labelComponent={
          <VictoryLabel
            backgroundStyle={{
              fill: Colors.background.INV_PIE_CHART_COUNT_LABEL,
            }}
            backgroundPadding={8}
            style={[{ fill: Colors.text.DARK_GRAY, fontSize: 25 }]}
          />
        }
        text={({ datum }) =>
          `${convertCountToLocaleString(
            datum.vinCount,
          )}\n(${calculatePercentage(datum.vinCount, datum.total)}%)`
        }
      />
    </g>
  );
};

CustomTooltip.defaultEvents = VictoryTooltip.defaultEvents;

type InitialETALocations = {
  id: number;
  name: string;
  code: string;
  ladName: null;
  vinCount: number;
};

type GateReleaseThresholdParamsType = {
  solutionId: string;
  fetchLocations: (searchOption: string) => void;
  isLocationsLoading: boolean;
  locationsData: InitialETALocations[];
  setSearchFilter: (queryKey: string, value: string | string[]) => void;
  redirectToDetailsView: (solutionId: string, locationId: number) => void;
  resetSearchAndFilters: () => void;
};

const PAGE_SIZE = 10;

export const GateReleaseThreshold = ({
  solutionId,
  fetchLocations,
  isLocationsLoading,
  locationsData,
  setSearchFilter,
  redirectToDetailsView,
  resetSearchAndFilters,
}: GateReleaseThresholdParamsType) => {
  const { ref, size } = useResizeDetector();
  const { t } = useTranslation("inv-dashboard");
  const [show, setShow] = useState(false);
  const [filter, setFilterData] = useState({
    name: null,
    queryKey: null,
    searchOption: null,
  });

  const maxWidth = 400;
  const width =
    size.width && size.width > maxWidth
      ? maxWidth
      : size.width
      ? size.width
      : maxWidth;
  const height = width;
  const outerRadius = Math.min(width, height) / 2;
  const innerRadiusRatio = 0.5; // Example ratio
  const innerRadius = outerRadius * innerRadiusRatio;

  const {
    pieChartData,
    isFetching: isLoading,
    isError,
  } = useGateReleaseThresholdsPieChart();

  const subTitle = t("inv-dashboard:On-Site Shippable VINs");
  const title = t("inv-dashboard:Gate Release Thresholds");
  const popupTitle = t(
    "inv-dashboard:Gate Release Thresholds: [[[filterName]]] (Shippable On Site VINs)",
    { filterName: filter?.name, keySeparator: ":" },
  );

  const pieChartStyle = {
    width: width,
    height: height,
    innerRadius: innerRadius,
    padding: { top: 60, right: 50, left: 0 },
    label: {
      labels: { fontSize: 10 },
    },
  };

  return (
    <>
      <div ref={ref}>
        {isLoading ? (
          <Loader loaded={false} />
        ) : (
          !isError && (
            <PieChartWithLegends
              widgetData={pieChartData}
              subTitle={subTitle}
              title={title}
              x={"type"}
              y={"vinCount"}
              onClickHandler={(queryKey, searchOption, props) => {
                const data = {
                  queryKey: queryKey,
                  name: props.datum.name,
                  searchOption: searchOption,
                  filterProps: props,
                };
                setFilterData(data);
                setShow(true);
                fetchLocations(searchOption);
              }}
              queryKey={QUERY_KEY_MAPPING.TIME_ON_SITE}
              labelComponent={<CustomTooltip />}
              pieChartStyle={pieChartStyle}
              addSpacerAfterTitle={true}
            />
          )
        )}
        <Alert show={isError}>
          {t("inv-dashboard:Unable to fetch details. Please try again later.")}
        </Alert>
      </div>
      <Modal
        backdrop={"static"}
        show={show}
        onHide={() => {
          setFilterData({
            name: null,
            searchOption: null,
            queryKey: null,
          });
          setShow(false);
        }}
        size="lg"
      >
        <Modal.Header closeButton className="bg-light text-body">
          <Text
            size={FontSize.size18}
            style={{ marginBottom: 2 }}
            as="div"
            bold
          >
            {t("inv-dashboard:Details")}
          </Text>
        </Modal.Header>
        <Modal.Body>
          <div css={{ minHeight: 250 }}>
            {isLocationsLoading ? (
              <Loader loaded={false} />
            ) : (
              locationsData.length > 0 && (
                <InventoryLocationsList
                  inventoryItemsList={locationsData}
                  title={popupTitle}
                  displayPaginationControls={true}
                  pageSize={PAGE_SIZE}
                  locationRowClickHandler={(locationId) => {
                    if (filter?.searchOption) {
                      resetSearchAndFilters();
                      setSearchFilter(QUERY_KEY_MAPPING.SHIPPABILITY, "true");
                      setSearchFilter(
                        QUERY_KEY_MAPPING.GATE_RELEASE_THRESHOLD,
                        [filter.searchOption],
                      );
                      setSearchFilter(
                        QUERY_KEY_MAPPING.TIME_ON_SITE,
                        TimeOnSiteDuration.ALL,
                      );
                      redirectToDetailsView(solutionId, locationId);
                    }
                  }}
                />
              )
            )}
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};
